import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  CpuChipIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  FireIcon,
  SignalIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';



const ESTADO_BADGE = {
  /* nuevos estados del móvil */
  en_camino_compania: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400', txt: 'En camino a compañía' },
  en_compania:        { bg: 'bg-teal-50',   text: 'text-teal-700',   dot: 'bg-teal-500',   txt: 'En compañía'       },
  en_camino_escena:   { bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500',    txt: 'En camino a escena'},
  /* estados legacy */
  en_camino:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400',  txt: 'En Camino'     },
  en_escena:     { bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500',   txt: 'En Escena'     },
  disponible:    { bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500',   txt: 'Disponible'    },
  no_disponible: { bg: 'bg-gray-50',    text: 'text-gray-500',    dot: 'bg-gray-400',    txt: 'No Disponible' },
  llegare_tarde: { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-400',  txt: 'Llegará Tarde' },
};

const incBadge = (estado = '') => {
  const e = estado.toLowerCase();
  if (e === 'cerrado')   return { bg: 'bg-gray-50',   text: 'text-gray-500',    dot: '#9ca3af', txt: 'Cerrado'    };
  if (e.includes('curso') || e.includes('atencion') || e.includes('atención'))
    return { bg: 'bg-red-50',    text: 'text-red-700',    dot: '#ef4444', txt: 'En Atención' };
  if (e.includes('camino'))
    return { bg: 'bg-orange-50', text: 'text-orange-700', dot: '#f97316', txt: 'En Camino'   };
  if (e.includes('escena'))
    return { bg: 'bg-green-50',  text: 'text-green-700',  dot: '#22c55e', txt: 'En Escena'   };
  if (e.includes('pendiente'))
    return { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: '#f59e0b', txt: 'Pendiente'   };
  return { bg: 'bg-gray-50', text: 'text-gray-600', dot: '#9ca3af', txt: estado };
};

const RESP_META = {
  en_camino_compania: { label: 'En camino a compañía',    color: '#e67e22' },
  en_compania:        { label: 'En compañía',              color: '#0d9488' },
  en_camino_escena:   { label: 'En camino a escena',       color: '#c0392b' },
  en_camino:          { label: 'En camino',                color: '#22c55e' },
  en_escena:          { label: 'En escena',                color: '#3b82f6' },
  llegare_tarde:      { label: 'Llegaré tarde',            color: '#f97316' },
  no_disponible:      { label: 'No disponible',            color: '#ef4444' },
  disponible:         { label: 'Disponible',               color: '#9ca3af' },
};

const ESTADOS_ACTIVOS_RESP = ['en_camino','en_escena','en_camino_compania','en_compania','en_camino_escena'];

const PRIORIDAD_CFG = {
  critica: { label: 'Crítica', bg: '#fde8e8', color: '#7b0000', border: '#c0392b' },
  alta:    { label: 'Alta',    bg: '#fde8e8', color: '#c0392b', border: '#e74c3c' },
  media:   { label: 'Media',   bg: '#fef3e8', color: '#b7770d', border: '#e67e22' },
  baja:    { label: 'Baja',    bg: '#eafaf1', color: '#1a7a3c', border: '#27ae60' },
};

const initials = (str) =>
  (str || '?').trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

export default function Dashboard() {
  const { sesion } = useAuth();
  const codCompania = sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : 44;
  const nombreCia   = sesion?.nombre_compania || `Cía. ${codCompania}`;

  const [servicios,   setServicios]   = useState([]);   // local DB
  const [respuestas,  setRespuestas]  = useState([]);
  const [hora,        setHora]        = useState(new Date());
  const [cargando,    setCargando]    = useState(true);

  const { socket } = useSocket();

  useEffect(() => {
    const t = setInterval(() => setHora(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    const [rSvc, rResp] = await Promise.allSettled([
      api.get('/servicios'),
      api.get('/respuestas'),
    ]);
    setServicios(rSvc.status === 'fulfilled' ? (rSvc.value.data || []) : []);
    setRespuestas(rResp.status === 'fulfilled' ? (rResp.value.data || []) : []);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!socket) return;
    const onResp = (data) => setRespuestas(prev => {
      const idx = prev.findIndex(r => r.carnet === data.carnet && r.servicioId === data.servicioId);
      return idx >= 0 ? prev.map((r, i) => i === idx ? data : r) : [...prev, data];
    });
    const onNuevo = (inc) => setServicios(prev => [inc, ...prev].slice(0, 100));
    const onAct   = (inc) => setServicios(prev => prev.map(s => s.id === inc.id ? { ...s, ...inc } : s));

    socket.on('respuesta_bombero',     onResp);
    socket.on('nuevo_incidente',       onNuevo);
    socket.on('incidente_actualizado', onAct);
    return () => {
      socket.off('respuesta_bombero',     onResp);
      socket.off('nuevo_incidente',       onNuevo);
      socket.off('incidente_actualizado', onAct);
    };
  }, [socket]);

  const fmt = (d) => d.toLocaleString('es-GT', { weekday:'short', day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  const fmtHora = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';

  // ── Stats ────────────────────────────────────────────────────────────────────
  const activos      = servicios.filter(s => s.estado !== 'cerrado').length;
  const cerrados     = servicios.filter(s => s.estado === 'cerrado').length;
  const respondiendo = respuestas.filter(r => ESTADOS_ACTIVOS_RESP.includes(r.estado)).length;

  // ── Gráfica tipos ─────────────────────────────────────────────────────────
  const TIPO_COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#6b7280'];
  const dataTipo = Object.entries(
    servicios.reduce((acc, s) => {
      const t = s.Des_Tipo_Servicio || s.Des_Clase_Servicio || 'Sin tipo';
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value], i) => ({
    name, value, color: TIPO_COLORS[i % TIPO_COLORS.length],
  }));

  // ── Gráfica respuestas ────────────────────────────────────────────────────
  const dataRespuestas = Object.entries(
    respuestas.reduce((acc, r) => { acc[r.estado] = (acc[r.estado] || 0) + 1; return acc; }, {})
  ).map(([estado, total]) => ({
    estado: RESP_META[estado]?.label || estado,
    total,
    color:  RESP_META[estado]?.color || '#9ca3af',
  }));

  const recientes = [...servicios].sort((a,b) => new Date(b.Fecha_Servicio||b.createdAt) - new Date(a.Fecha_Servicio||a.createdAt)).slice(0, 8);

  return (
    <div className="p-5 bg-[#eef0f5] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            🚒 Centro de Operaciones
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">{fmt(hora)} — {nombreCia}</p>
        </div>
        <div className="flex gap-2.5">
          <Button variant="outline" size="sm" asChild>
            <Link to="/prediccion"><CpuChipIcon className="h-4 w-4" /> Predicción IA</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/incidentes"><PlusIcon className="h-4 w-4" /> Nuevo Incidente</Link>
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">

        <div className="rounded-2xl shadow-lg overflow-hidden relative" style={{ background: 'linear-gradient(135deg,#c0392b,#7b0000)' }}>
          <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
          <div className="p-5 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                <FireIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white/50 text-[9px] uppercase tracking-widest font-bold">en curso</span>
            </div>
            <div className="text-4xl font-black text-white leading-none mb-1">{cargando ? '—' : activos}</div>
            <div className="text-white/60 text-xs font-medium">Incidentes activos</div>
          </div>
        </div>

        <div className="rounded-2xl shadow-lg overflow-hidden relative" style={{ background: 'linear-gradient(135deg,#27ae60,#145a32)' }}>
          <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
          <div className="p-5 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white/50 text-[9px] uppercase tracking-widest font-bold">activos</span>
            </div>
            <div className="text-4xl font-black text-white leading-none mb-1">{cargando ? '—' : respondiendo}</div>
            <div className="text-white/60 text-xs font-medium">Bomberos respondiendo</div>
          </div>
        </div>

        <div className="rounded-2xl shadow-lg overflow-hidden relative" style={{ background: 'linear-gradient(135deg,#1565c0,#0a3880)' }}>
          <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
          <div className="p-5 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white/50 text-[9px] uppercase tracking-widest font-bold">total</span>
            </div>
            <div className="text-4xl font-black text-white leading-none mb-1">{cargando ? '—' : servicios.length}</div>
            <div className="text-white/60 text-xs font-medium">Servicios registrados</div>
          </div>
        </div>

        <div className="rounded-2xl shadow-lg overflow-hidden relative" style={{ background: 'linear-gradient(135deg,#0d9488,#065f56)' }}>
          <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
          <div className="p-5 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white/50 text-[9px] uppercase tracking-widest font-bold">finalizados</span>
            </div>
            <div className="text-4xl font-black text-white leading-none mb-1">{cargando ? '—' : cerrados}</div>
            <div className="text-white/60 text-xs font-medium">Servicios cerrados</div>
          </div>
        </div>
      </div>

      {/* GRÁFICAS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">

        {/* Donut — tipos */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
          <div className="bg-gradient-to-r from-[#1a1a2e] via-[#7b1010] to-[#c0392b] px-5 py-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <FireIcon className="h-4 w-4 text-red-300" />
                <span className="text-white text-sm font-bold tracking-wide">Servicios por tipo</span>
              </div>
              <p className="text-white/50 text-[10px]">Distribución por categoría</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-white leading-none">{servicios.length}</div>
              <div className="text-white/50 text-[10px] mt-0.5">registrados</div>
            </div>
          </div>
          <div className="p-6 bg-gradient-to-br from-white to-red-50/20">
            {dataTipo.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-14">Sin servicios registrados</p>
            ) : (
              <div className="flex items-start gap-5">
                <div className="relative flex-shrink-0 w-[200px] h-[200px]">
                  <div className="absolute inset-[-10px] rounded-full border-2 border-dashed border-red-200/60" style={{ animation: 'spin 25s linear infinite' }} />
                  <div style={{ filter: 'drop-shadow(0 0 14px rgba(192,57,43,0.30))' }} className="absolute inset-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={dataTipo} cx="50%" cy="50%" innerRadius={62} outerRadius={90} paddingAngle={2} dataKey="value" animationDuration={900} stroke="none">
                          {dataTipo.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ fontSize: 11, borderRadius: 10, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,.22)', background: '#1a1a2e', color: '#fff' }} itemStyle={{ color: '#fff' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-4xl font-black text-gray-900 leading-none">{servicios.length}</span>
                    <span className="text-[11px] text-gray-400 font-medium mt-1">servicios</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2 pt-1">
                  {dataTipo.map((item, i) => {
                    const pct = servicios.length > 0 ? Math.round((item.value / servicios.length) * 100) : 0;
                    return (
                      <div key={i} className="rounded-xl p-2.5 border" style={{ background: `${item.color}0d`, borderColor: `${item.color}30` }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-gray-700 font-semibold truncate max-w-[120px]">{item.name}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[10px] text-gray-400">{pct}%</span>
                            <span className="text-[11px] font-black text-white rounded-full px-1.5 py-0.5 leading-none" style={{ background: item.color }}>{item.value}</span>
                          </div>
                        </div>
                        <div className="h-1.5 rounded-full bg-black/5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(to right, ${item.color}77, ${item.color})`, transition: 'width 1s cubic-bezier(.4,0,.2,1)' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Respuestas del personal */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
          <div className="bg-gradient-to-r from-[#0f3460] via-[#1a1a6e] to-[#1a1a2e] px-5 py-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <UserGroupIcon className="h-4 w-4 text-blue-300" />
                <span className="text-white text-sm font-bold tracking-wide">Respuestas del personal</span>
              </div>
              <p className="text-white/50 text-[10px]">Disponibilidad en tiempo real</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-white leading-none">{respuestas.length}</div>
              <div className="text-white/50 text-[10px] mt-0.5">bomberos</div>
            </div>
          </div>
          <div className="p-6 bg-gradient-to-br from-white to-blue-50/20">
            {dataRespuestas.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-14">Sin respuestas registradas</p>
            ) : (
              <div className="space-y-3">
                {dataRespuestas.map((item, i) => {
                  const pct = respuestas.length > 0 ? Math.round((item.total / respuestas.length) * 100) : 0;
                  return (
                    <div key={i} className="rounded-xl p-3.5 border" style={{ background: `${item.color}0d`, borderColor: `${item.color}30` }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-gray-800">{item.estado}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-400">{pct}%</span>
                          <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-black text-white" style={{ background: item.color }}>{item.total}</div>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full bg-black/5 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(to right, ${item.color}55, ${item.color})`, transition: 'width 1s cubic-bezier(.4,0,.2,1)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">

        {/* COLUMNA IZQUIERDA */}
        <div className="flex flex-col gap-5">

          {/* MAPA */}
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="bg-gradient-to-r from-[#1a1a2e] to-[#2d2d4e] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13V7m0 13l6-3m-6-10l6-3m0 0l5.447 2.724A1 1 0 0121 7.618v10.764a1 1 0 01-1.447.894L15 17m0-13v13"/>
                </svg>
                <div>
                  <div className="text-white text-sm font-bold">Mapa de Operaciones</div>
                  <div className="text-white/40 text-[10px]">Jalapa, Guatemala</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 bg-green-500/20 text-green-300 text-[10px] font-bold px-3 py-1 rounded-full">
                <SignalIcon className="h-3 w-3" style={{ animation: 'live-pulse 1.5s infinite' }} /> EN VIVO
              </div>
            </div>
            <iframe className="w-full h-72 sm:h-80 border-none block" title="Mapa Jalapa"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-90.0039%2C14.6156%2C-89.9639%2C14.6556&layer=mapnik&marker=14.6356%2C-89.9839"
            />
          </div>

          {/* PERSONAL RESPONDIENDO */}
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="bg-gradient-to-r from-[#1a4731] to-[#1a1a2e] px-5 py-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <UserGroupIcon className="h-4 w-4 text-green-300" />
                  <span className="text-white text-sm font-bold">Personal Respondiendo</span>
                </div>
                <p className="text-white/40 text-[10px]">Bomberos en servicio activo</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-white leading-none">{respondiendo}</div>
                <div className="text-white/40 text-[10px] mt-0.5">activos</div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-green-50/20">
              {respuestas.length === 0 ? (
                <p className="py-8 text-center text-gray-400 text-sm">{cargando ? 'Cargando...' : 'Sin respuestas registradas'}</p>
              ) : respuestas.slice(0, 8).map((r, i) => {
                const b = ESTADO_BADGE[r.estado] || { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400', txt: r.estado };
                return (
                  <div key={r.carnet || i} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors">
                    <div className="h-9 w-9 rounded-xl bg-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                      {initials(r.nombre)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{r.nombre || 'Bombero'}</div>
                      <div className="text-[10px] text-gray-400">#{r.carnet}</div>
                    </div>
                    <span className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-bold whitespace-nowrap', b.bg, b.text)}>
                      <span className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', b.dot)} />
                      {b.txt}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA */}
        <div className="flex flex-col gap-5">

          {/* ACCIONES RÁPIDAS */}
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="bg-gradient-to-r from-[#1a1a2e] to-[#2d2d4e] px-5 py-4">
              <span className="text-white text-sm font-bold">⚡ Acciones Rápidas</span>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3 bg-white">
              {[
                { to: '/incidentes', emoji: '🚨', label: 'Nuevo Incidente',  color: '#c0392b' },
                { to: '/despacho',   emoji: '🗺️', label: 'Ver Despacho',    color: '#1565c0' },
                { to: '/respuestas', emoji: '💬', label: 'Respuestas',       color: '#27ae60' },
                { to: '/historial',  emoji: '📋', label: 'Historial',        color: '#6c757d' },
              ].map(({ to, emoji, label, color }) => (
                <Link key={to} to={to} style={{ textDecoration: 'none' }}>
                  <div className="rounded-xl p-3.5 border hover:shadow-md transition-all cursor-pointer text-center" style={{ borderColor: `${color}30`, background: `${color}08` }}>
                    <div className="text-2xl mb-1">{emoji}</div>
                    <div className="text-xs font-bold" style={{ color }}>{label}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* INCIDENTES RECIENTES */}
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="bg-gradient-to-r from-[#7b1010] to-[#1a1a2e] px-5 py-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <ExclamationCircleIcon className="h-4 w-4 text-red-300" />
                  <span className="text-white text-sm font-bold">Servicios Recientes</span>
                </div>
                <p className="text-white/40 text-[10px]">Últimos registrados</p>
              </div>
              <Link to="/incidentes" className="bg-white/15 hover:bg-white/25 text-white text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors no-underline">
                + Nuevo
              </Link>
            </div>
            <div className="bg-gradient-to-br from-white to-red-50/10">
              {recientes.length === 0 ? (
                <p className="py-8 text-center text-gray-400 text-sm">{cargando ? 'Cargando...' : 'Sin servicios registrados'}</p>
              ) : recientes.map(s => {
                const b   = incBadge(s.estado);
                const pr  = (s.Prioridad || 'media').toLowerCase();
                const prC = PRIORIDAD_CFG[pr] || PRIORIDAD_CFG.media;
                const tipo = s.Des_Tipo_Servicio || s.Des_Clase_Servicio || '—';
                return (
                  <Link to="/despacho" key={s.id} className="flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 transition-colors no-underline">
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0 mt-0.5"
                      style={{ background: prC.bg, color: prC.color }}>
                      {prC.label.slice(0,1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{tipo}</div>
                      <div className="text-[10px] text-gray-400 truncate">{s.Direccion || '—'}</div>
                      <div className="text-[10px] text-gray-300 mt-0.5">{fmtHora(s.Fecha_Servicio || s.createdAt)}</div>
                    </div>
                    <span className={cn('px-2 py-1 rounded-xl text-[10px] font-bold whitespace-nowrap mt-0.5 flex-shrink-0', b.bg, b.text)}>
                      {b.txt}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
